/**
 * Mensagens trocadas entre os aparelhos e o servidor (WebSocket, JSON).
 * O aparelho só envia intenções; o servidor decide tudo e devolve o estado.
 */
import type { GameEvent, Mode, PublicState, ThemeId } from '@tigrinho/engine';

export const PROTOCOL_VERSION = 2;

/** Ações de jogo que um aparelho pode pedir. O servidor preenche quem é o jogador. */
export type ClientAction =
  | { type: 'ROLL' }
  | { type: 'PAY_JAIL' }
  /** Lojinha: comprar uma carta (na sua vez, antes de rolar). */
  | { type: 'BUY_CARD'; card: string }
  /** MST invadiu sua cidade: chamar o fazendeiro (pago) ou deixar perder um nível. */
  | { type: 'EXPEL_MST' }
  | { type: 'ACCEPT_MST' }
  /** "Faz o L": pagar o imposto normalmente. */
  | { type: 'PAY_TAX' }
  /** "Faz o L": tentar sonegar (os dados são do servidor). */
  | { type: 'EVADE_TAX' }
  | { type: 'USE_JAIL_CARD' }
  /** Comprar/construir até este nível: 0 bandeirola, 1-3 casas, 4 hotel. */
  | { type: 'BUILD'; level: number }
  | { type: 'SKIP_BUILD' }
  /** Comprar a cidade de outro jogador pelo dobro. */
  | { type: 'TAKEOVER' }
  | { type: 'SKIP_TAKEOVER' }
  /** Escolher a cidade do Campeonato Mundial. */
  | { type: 'HOST'; square: number }
  /** Volta ao Mundo: voar para uma casa. */
  | { type: 'TOUR'; to: number }
  | { type: 'SKIP_TOUR' }
  | { type: 'CHOOSE'; choice: string }
  /** Vender uma propriedade ao banco (para pagar dívida). */
  | { type: 'SELL'; square: number }
  | { type: 'PAY_DEBT' }
  | { type: 'DECLARE_BANKRUPTCY' }
  | { type: 'AUTO_SETTLE_DEBT' }
  | { type: 'END_TURN' };

/** Itens do Arsenal de Provocação. */
export const TAUNT_ITEMS = ['tomate', 'ovo', 'chinelo', 'torta', 'boleto', 'risada', 'caveira', 'palhaco', 'choro', 'fogo', 'coroa'] as const;
export type TauntItem = (typeof TAUNT_ITEMS)[number];

/** Frases rápidas (o texto fica no aparelho). */
export const PHRASES = ['pagaLogo', 'choraNao', 'f', 'vinganca', 'taCaro', 'bomDia', 'socorro', 'gg'] as const;
export type Phrase = (typeof PHRASES)[number];

export const PAWN_COUNT = 6;

export type ClientMsg =
  /** `claim`: retomar o lugar de um jogador desconectado (ex.: trocou do celular para o notebook). */
  | { t: 'hello'; token: string; name?: string; protocol?: number; claim?: string }
  | { t: 'setName'; name: string }
  | { t: 'setPawn'; pawn: number }
  | { t: 'ready'; ready: boolean }
  | { t: 'settings'; mode?: Mode; theme?: ThemeId }
  | { t: 'addBot' }
  | { t: 'removeSeat'; seat: string }
  | { t: 'leave' }
  | { t: 'start' }
  | { t: 'act'; action: ClientAction }
  | { t: 'taunt'; target: string; item: TauntItem }
  | { t: 'phrase'; phrase: Phrase }
  | { t: 'rematch' }
  | { t: 'ping' };

export interface PublicSeat {
  id: string;
  name: string;
  pawn: number;
  ready: boolean;
  bot: boolean;
  connected: boolean;
  /** Jogador humano ausente que está sendo jogado pelo bot. */
  botControlled: boolean;
}

export type TimerKind = 'roll' | 'endTurn' | 'build' | 'takeover' | 'championship' | 'tour' | 'card' | 'debt' | 'tax' | 'mst';

export interface PublicTimer {
  kind: TimerKind;
  /** Horário do servidor (ms) em que a ação padrão acontece. */
  deadline: number;
  seconds: number;
}

export interface TauntStats {
  thrown: number;
  received: number;
}

export interface PublicRoom {
  code: string;
  status: 'lobby' | 'playing' | 'finished';
  hostId: string | null;
  settings: { mode: Mode; theme: ThemeId };
  seats: PublicSeat[];
  game: PublicState | null;
  timer: PublicTimer | null;
  clock: { elapsedMs: number; durationMs: number; paused: boolean } | null;
  /** Prazo de resposta de cada proposta de troca (id → horário do servidor). */
  tradeDeadlines: Record<number, number>;
  taunts: Record<string, TauntStats>;
  seq: number;
  serverNow: number;
  rulesVersion: string;
}

export type ServerMsg =
  | { t: 'welcome'; you: string | null; room: PublicRoom }
  | { t: 'room'; room: PublicRoom; events: GameEvent[] }
  | { t: 'taunt'; from: string; target: string; item: TauntItem; combo: number }
  | { t: 'phrase'; from: string; phrase: Phrase }
  | { t: 'error'; code: string; message: string }
  | { t: 'pong'; now: number };

export interface RoomInfo {
  exists: boolean;
  code: string;
  status?: PublicRoom['status'];
  players?: number;
  mode?: Mode;
  theme?: ThemeId;
  host?: string;
  /** Jogadores desconectados cujo lugar pode ser retomado (partida em andamento). */
  claimable?: { id: string; name: string }[];
}

// ---------------------------------------------------------------------------
// Tempo de apresentação: quanto a tela leva para mostrar uma jogada (dados rolando, peão
// andando, carta sendo lida e depois o efeito dela). O servidor espera esse tempo antes de
// contar o cronômetro e antes de um bot jogar, para ninguém "passar na frente" da animação.

/** Os dados voam e quicam antes do resultado aparecer. */
export const ROLL_MS = 1300;
/** Cada passo do peão. */
export const STEP_MS = 170;
/** Voo (Volta ao Mundo, cartas que teleportam). */
export const FLY_MS = 750;
/** A carta aparece e fica na tela para ser lida antes do efeito. */
export const CARD_READ_MS = 3000;
/** Dado especial do Ímã do Azarão (gira e mostra se puxa ou não). */
export const MAGNET_MS = 1500;
/** Dados da sonegação rolando antes do veredito. */
export const EVADE_MS = 1700;
/** MST entrando pelo canto da tela e marchando até a cidade. */
export const MST_MARCH_MS = 2600;
/** Fazendeiro chegando com o cachorro e a liminar; turma saindo correndo. */
export const MST_EXPEL_MS = 2400;
/** Turma derrubando um nível da cidade. */
export const MST_INVADE_MS = 1400;
/** "Sorteando quem começa…" no início da partida. */
export const ORDER_DRAW_MS = 3200;

/** Quanto a tela leva para mostrar estes movimentos. */
export function walkMs(events: GameEvent[], boardSize = 32): number {
  let ms = 0;
  for (const e of events) {
    if (e.type === 'moved') {
      if (e.how === 'walk') ms += ((e.to - e.from + boardSize) % boardSize) * STEP_MS;
      else if (e.how === 'back') ms += ((e.from - e.to + boardSize) % boardSize) * STEP_MS;
      else ms += FLY_MS;
    } else if (e.type === 'traveled') ms += FLY_MS;
    else if (e.type === 'magnetRolled') ms += MAGNET_MS;
    else if (e.type === 'taxEvasion') ms += EVADE_MS;
    else if (e.type === 'mstArrived') ms += MST_MARCH_MS;
    else if (e.type === 'mstExpelled') ms += MST_EXPEL_MS;
    else if (e.type === 'mstInvaded') ms += MST_INVADE_MS;
  }
  return ms > 0 ? ms + 250 : 0;
}

/** Divide os eventos na carta: o que acontece antes dela e o efeito, mostrado só depois. */
export function splitAtCard(events: GameEvent[]): [GameEvent[], GameEvent[]] {
  const i = events.findIndex((e) => e.type === 'cardDrawn');
  return i < 0 ? [events, []] : [events.slice(0, i + 1), events.slice(i + 1)];
}

/** Tempo total de apresentação de uma mensagem com estes eventos. */
export function presentationMs(events: GameEvent[], boardSize = 32): number {
  let ms = 0;
  if (events.some((e) => e.type === 'gameStarted')) ms += ORDER_DRAW_MS;
  if (events.some((e) => e.type === 'diceRolled')) ms += ROLL_MS;
  const [before, after] = splitAtCard(events);
  ms += walkMs(before, boardSize);
  if (after.length || before.some((e) => e.type === 'cardDrawn')) ms += CARD_READ_MS + walkMs(after, boardSize);
  return ms;
}
